import React from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { socialPlatforms } from '../utils/socialPlatforms';
import {
  AVATAR_SHAPES, LINK_CARD_STYLES, BG_EFFECTS, ORBIT_SPEEDS, ORBIT_DIRECTIONS
} from '../utils/customisation';
import { Sparkles, Lock, Crown, Upload, X, Image as ImageIcon } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const ProBadge = () => (
  <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 dark:bg-amber-950/50 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
    <Crown className="w-2.5 h-2.5" /> Pro
  </span>
);

const FreeBadge = () => (
  <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-400 px-1.5 py-0.5 rounded-full">
    Free
  </span>
);

const CornerTab = ({ pro }) => (
  <span className="absolute top-1.5 left-1.5 z-10">
    {pro ? <ProBadge /> : <FreeBadge />}
  </span>
);

const OptionCard = ({ children, active, onClick, locked, isPro, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={locked}
    data-testid={testid}
    className={`relative p-3 pt-6 rounded-xl border text-left transition-all ${
      active ? 'border-primary bg-primary/5 ring-2 ring-primary/30' : 'border-border hover:border-primary/40'
    } ${locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <CornerTab pro={isPro} />
    {locked && (
      <span className="absolute top-1.5 right-1.5 text-amber-500">
        <Lock className="w-3 h-3" />
      </span>
    )}
    {children}
  </button>
);

const V2CustomisationBlock = ({ formData, setFormData, isPro, links = [] }) => {
  // Unique platforms from user's saved links (used to pre-populate the Orbit Header filter)
  const userPlatforms = React.useMemo(() => {
    const seen = new Map();
    for (const l of links) {
      if (!l.platform || seen.has(l.platform)) continue;
      const p = socialPlatforms.find(sp => sp.id === l.platform);
      if (p) seen.set(l.platform, p);
    }
    return Array.from(seen.values());
  }, [links]);

  const orbitPlatforms = formData.orbit_platforms || [];
  const togglePlatform = (id) => {
    const next = orbitPlatforms.includes(id)
      ? orbitPlatforms.filter(p => p !== id)
      : [...orbitPlatforms, id];
    setFormData({ ...formData, orbit_platforms: next });
  };

  return (
    <div className="p-4 rounded-xl border border-border space-y-5" data-testid="v2-customisation">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-primary" />
        <Label className="font-semibold">More customisation</Label>
      </div>

      {/* Avatar Shape */}
      <div>
        <Label className="text-xs mb-2 block">Avatar shape</Label>
        <div className="grid grid-cols-3 gap-2">
          {AVATAR_SHAPES.map(s => (
            <OptionCard
              key={s.id}
              active={(formData.avatar_shape || 'round') === s.id}
              onClick={() => {
                const patch = { avatar_shape: s.id };
                // Full-width-square + orbit are mutually exclusive (orbit needs
                // a centred small avatar). If user picks full_square while orbit
                // is on, turn orbit off and tell them why.
                if (s.fullWidth && formData.orbit_enabled) {
                  patch.orbit_enabled = false;
                  toast.info('Orbital icons turned off — they need a round avatar to circle around.');
                }
                setFormData({ ...formData, ...patch });
              }}
              locked={s.pro && !isPro}
              isPro={!!s.pro}
              testid={`avatar-shape-${s.id}`}
            >
              <div className="flex flex-col items-center gap-1">
                {s.fullWidth ? (
                  // Render preview as a wide rectangle hinting at full-width banner
                  <div className="w-12 h-7 bg-gradient-to-b from-primary to-primary/40 rounded-sm" />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/60"
                    style={{
                      borderRadius: s.borderRadius,
                      ...(s.clipPath ? { clipPath: s.clipPath } : {}),
                    }}
                  />
                )}
                <span className="text-[10px] text-center leading-tight">{s.name}</span>
              </div>
            </OptionCard>
          ))}
        </div>
      </div>

      {/* Link Card Style */}
      <div>
        <Label className="text-xs mb-2 block">Link card style</Label>
        <div className="grid grid-cols-3 gap-2">
          {LINK_CARD_STYLES.map(s => (
            <OptionCard
              key={s.id}
              active={(formData.link_card_style || 'classic') === s.id}
              onClick={() => setFormData({ ...formData, link_card_style: s.id })}
              locked={s.pro && !isPro}
              isPro={!!s.pro}
              testid={`link-card-${s.id}`}
            >
              <p className="text-sm font-medium">{s.name}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{s.description}</p>
            </OptionCard>
          ))}
        </div>
      </div>

      {/* Background Effect */}
      <div>
        <Label className="text-xs mb-2 block">Background effect</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {BG_EFFECTS.map(e => (
            <OptionCard
              key={e.id}
              active={(formData.bg_effect || 'none') === e.id}
              onClick={() => setFormData({ ...formData, bg_effect: e.id })}
              locked={e.pro && !isPro}
              isPro={!!e.pro}
              testid={`bg-effect-${e.id}`}
            >
              <p className="text-sm font-medium">{e.name}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{e.description}</p>
            </OptionCard>
          ))}
        </div>
      </div>

      {/* Custom Wallpaper (Pro) */}
      <CustomWallpaperCard formData={formData} setFormData={setFormData} isPro={isPro} />

      {/* Orbit Header (Pro) */}
      <div className="p-3 rounded-xl bg-muted/40 border border-border">
        <div className="flex items-center justify-between mb-2">
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              Orbit Header {!isPro && <ProBadge />}
            </Label>
            <p className="text-[11px] text-muted-foreground">Your avatar in the centre, your social icons orbiting around it.</p>
          </div>
          <button
            type="button"
            disabled={!isPro}
            onClick={() => {
              const turningOn = !formData.orbit_enabled;
              const patch = { orbit_enabled: turningOn };
              // Orbit needs a round avatar to circle. If user turns orbit on
              // while avatar is set to full-width-square, swap back to round.
              if (turningOn && formData.avatar_shape === 'full_square') {
                patch.avatar_shape = 'round';
                toast.info('Avatar shape reset to Round — orbital icons need a centred avatar to circle.');
              }
              setFormData({ ...formData, ...patch });
            }}
            className={`relative w-11 h-6 rounded-full transition-colors ${
              formData.orbit_enabled ? 'bg-primary' : 'bg-muted'
            } ${!isPro ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="orbit-toggle"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${formData.orbit_enabled ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        {formData.orbit_enabled && isPro && (
          <div className="space-y-3 mt-3">
            {/* Speed */}
            <div>
              <Label className="text-[11px] mb-1 block">Speed</Label>
              <div className="flex gap-2">
                {ORBIT_SPEEDS.map(s => (
                  <button key={s.id} type="button"
                    onClick={() => setFormData({ ...formData, orbit_speed: s.id })}
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      (formData.orbit_speed || 'normal') === s.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/40'
                    }`}
                    data-testid={`orbit-speed-${s.id}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            {/* Direction */}
            <div>
              <Label className="text-[11px] mb-1 block">Direction</Label>
              <div className="flex gap-2">
                {ORBIT_DIRECTIONS.map(d => (
                  <button key={d.id} type="button"
                    onClick={() => setFormData({ ...formData, orbit_direction: d.id })}
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      (formData.orbit_direction || 'cw') === d.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/40'
                    }`}
                    data-testid={`orbit-direction-${d.id}`}>
                    {d.name}
                  </button>
                ))}
              </div>
            </div>
            {/* Spotlight colour */}
            <div>
              <Label className="text-[11px] mb-1 block">Spotlight colour</Label>
              <div className="flex items-center gap-2">
                <Input type="color"
                  value={formData.orbit_spotlight_color || '#0055FF'}
                  onChange={(e) => setFormData({ ...formData, orbit_spotlight_color: e.target.value })}
                  className="h-9 w-16 p-1 cursor-pointer"
                  data-testid="orbit-spotlight-color" />
                <button type="button"
                  onClick={() => setFormData({ ...formData, orbit_spotlight_color: '' })}
                  className="text-[11px] text-muted-foreground hover:underline">
                  Use theme accent
                </button>
              </div>
            </div>
            {/* Platforms */}
            {userPlatforms.length > 0 && (
              <div>
                <Label className="text-[11px] mb-1 block">Icons to orbit ({orbitPlatforms.length === 0 ? 'all' : `${orbitPlatforms.length}/${userPlatforms.length}`})</Label>
                <div className="flex flex-wrap gap-1.5">
                  {userPlatforms.map(p => {
                    const Icon = p.icon;
                    const active = orbitPlatforms.length === 0 || orbitPlatforms.includes(p.id);
                    return (
                      <button key={p.id} type="button"
                        onClick={() => togglePlatform(p.id)}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] border transition-colors ${
                          active ? 'border-primary/60 bg-primary/5' : 'border-border opacity-50 hover:opacity-100'
                        }`}
                        data-testid={`orbit-platform-${p.id}`}>
                        {Icon && <Icon className="w-3 h-3" style={{ color: p.color }} />}
                        {p.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Select none to use every linked platform.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hide branding (Pro) */}
      <div className="p-3 rounded-xl bg-muted/40 border border-border flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold flex items-center gap-1.5">
            Hide LinkMonkey branding {!isPro && <ProBadge />}
          </Label>
          <p className="text-[11px] text-muted-foreground">Removes the "Powered by LinkMonkey" footer from your public profile.</p>
        </div>
        <button
          type="button"
          disabled={!isPro}
          onClick={() => setFormData({ ...formData, hide_branding: !formData.hide_branding })}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            formData.hide_branding ? 'bg-primary' : 'bg-muted'
          } ${!isPro ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid="hide-branding-toggle"
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${formData.hide_branding ? 'translate-x-5' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default V2CustomisationBlock;

/* ============================================================
   Custom Wallpaper card — Pro feature
   Upload an image, choose cover/tile, opacity 0-100%, blur 0-20px.
   ============================================================ */
const CustomWallpaperCard = ({ formData, setFormData, isPro }) => {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  const wallpaperUrl = formData.custom_wallpaper_url || null;
  const mode = formData.custom_wallpaper_mode || 'cover';
  const opacityVal = typeof formData.custom_wallpaper_opacity === 'number' ? formData.custom_wallpaper_opacity : 100;
  const blurVal = typeof formData.custom_wallpaper_blur === 'number' ? formData.custom_wallpaper_blur : 0;
  const scaleVal = typeof formData.custom_wallpaper_scale === 'number' ? formData.custom_wallpaper_scale : 100;

  // Local slider state — decouples drag-frequent re-renders from the parent
  // form state. The slider visually moves smoothly on every drag (cheap local
  // state) but only commits to formData on release (mouseup/touchend), so the
  // huge Settings tree doesn't re-render 60 times/sec with blur filters
  // applied. Previously caused near-freeze on slow devices.
  const [scale, setScale] = React.useState(scaleVal);
  const [opacity, setOpacity] = React.useState(opacityVal);
  const [blur, setBlur] = React.useState(blurVal);

  // Re-sync local state if parent prop changes (e.g. user resets to defaults
  // or loads from server).
  React.useEffect(() => { setScale(scaleVal); }, [scaleVal]);
  React.useEffect(() => { setOpacity(opacityVal); }, [opacityVal]);
  React.useEffect(() => { setBlur(blurVal); }, [blurVal]);

  const commit = (field, value) => setFormData({ ...formData, [field]: value });

  const onPick = () => inputRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isPro) {
      toast.error('Custom wallpapers are a Pro feature.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max file size is 5MB.');
      return;
    }
    setUploading(true);
    try {
      const result = await api.uploadImage('customWallpaper', file);
      const url = result.url || (`/api/files/${result.path}`);
      setFormData({ ...formData, custom_wallpaper_url: url, custom_wallpaper_mode: mode });
      toast.success('Wallpaper uploaded.');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async () => {
    try {
      await api.deleteImage('customWallpaper');
      setFormData({ ...formData, custom_wallpaper_url: null });
      toast.success('Wallpaper removed.');
    } catch (err) {
      toast.error(err.message || 'Could not remove wallpaper');
    }
  };

  // Background-size logic — combine cover/tile mode with the user's scale.
  const previewBgSize = (() => {
    if (mode === 'tile') return `${scale}% auto`;
    // Cover mode: scale 100% = cover the area; >100% zooms in further.
    if (scale === 100) return 'cover';
    return `${scale}% auto`;
  })();

  return (
    <div className="p-3 rounded-xl bg-muted/40 border border-border" data-testid="custom-wallpaper-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <Label className="text-sm font-semibold flex items-center gap-1.5">
            <ImageIcon className="w-4 h-4" />
            Custom wallpaper {!isPro && <ProBadge />}
          </Label>
          <p className="text-[11px] text-muted-foreground">Upload your own background image.</p>
        </div>
      </div>

      {/* Size recommendation */}
      <div className="mb-3 px-3 py-2 rounded-md bg-background/60 border border-border/60 text-[11px] leading-relaxed text-muted-foreground">
        <span className="font-medium text-foreground">Recommended:</span>{' '}
        portrait <strong className="text-foreground">1080×1920</strong> or landscape <strong className="text-foreground">1920×1080</strong>,
        JPG/PNG/WebP, under <strong className="text-foreground">5 MB</strong>.
        High contrast images look best — use the blur + intensity sliders below if it competes with your text.
      </div>

      {/* Preview — true skinny portrait (phone-like proportions). Width is
          driven from the height so it stays narrow regardless of card width. */}
      <div className="flex justify-center mb-3">
        <div
          className="relative h-[280px] aspect-[9/19] rounded-[1.5rem] border-[3px] border-zinc-800 overflow-hidden bg-muted shadow-md"
          style={wallpaperUrl ? {
            backgroundImage: `url(${wallpaperUrl})`,
            backgroundSize: previewBgSize,
            backgroundRepeat: mode === 'tile' ? 'repeat' : 'no-repeat',
            backgroundPosition: 'center',
            opacity: opacity / 100,
            filter: blur > 0 ? `blur(${blur}px)` : 'none',
          } : undefined}
          data-testid="custom-wallpaper-preview"
        >
          {!wallpaperUrl && (
            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-muted-foreground text-center px-3">
              No wallpaper uploaded
            </div>
          )}
        </div>
      </div>

      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onUpload} data-testid="custom-wallpaper-input" />

      <div className="flex gap-2 mb-3">
        <Button type="button" size="sm" variant="outline" disabled={!isPro || uploading} onClick={onPick}
          className="flex-1" data-testid="custom-wallpaper-upload-btn">
          <Upload className="w-3.5 h-3.5 mr-1.5" />
          {uploading ? 'Uploading…' : wallpaperUrl ? 'Replace' : 'Upload'}
        </Button>
        {wallpaperUrl && (
          <Button type="button" size="sm" variant="ghost" onClick={onRemove} data-testid="custom-wallpaper-remove-btn">
            <X className="w-3.5 h-3.5 mr-1.5" /> Remove
          </Button>
        )}
      </div>

      {wallpaperUrl && (
        <div className="space-y-3">
          {/* Mode toggle */}
          <div>
            <Label className="text-[11px] mb-1 block">Display mode</Label>
            <div className="flex gap-2">
              {[
                { id: 'cover', name: 'Cover', hint: 'Fill the page' },
                { id: 'tile', name: 'Tile', hint: 'Repeat as pattern' },
              ].map(m => (
                <button key={m.id} type="button"
                  onClick={() => setFormData({ ...formData, custom_wallpaper_mode: m.id })}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    mode === m.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/40'
                  }`}
                  data-testid={`custom-wallpaper-mode-${m.id}`}>
                  <div>{m.name}</div>
                  <div className="text-[10px] opacity-70 font-normal">{m.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Scale */}
          <div>
            <Label className="text-[11px] mb-1 flex items-center justify-between">
              <span>Scale ({scale}%)</span>
              <button type="button" onClick={() => { setScale(100); commit('custom_wallpaper_scale', 100); }}
                className="text-[10px] text-muted-foreground hover:text-foreground underline">Reset</button>
            </Label>
            <input type="range" min={50} max={200} step={5} value={scale}
              onChange={(e) => setScale(parseInt(e.target.value, 10))}
              onMouseUp={(e) => commit('custom_wallpaper_scale', parseInt(e.target.value, 10))}
              onTouchEnd={(e) => commit('custom_wallpaper_scale', parseInt(e.target.value, 10))}
              onKeyUp={(e) => commit('custom_wallpaper_scale', parseInt(e.target.value, 10))}
              className="w-full accent-primary cursor-pointer"
              data-testid="custom-wallpaper-scale" />
            <p className="text-[10px] text-muted-foreground mt-1">Zoom into your image to crop or fit the best part.</p>
          </div>

          {/* Opacity */}
          <div>
            <Label className="text-[11px] mb-1 block">Intensity ({opacity}%)</Label>
            <input type="range" min={10} max={100} value={opacity}
              onChange={(e) => setOpacity(parseInt(e.target.value, 10))}
              onMouseUp={(e) => commit('custom_wallpaper_opacity', parseInt(e.target.value, 10))}
              onTouchEnd={(e) => commit('custom_wallpaper_opacity', parseInt(e.target.value, 10))}
              onKeyUp={(e) => commit('custom_wallpaper_opacity', parseInt(e.target.value, 10))}
              className="w-full accent-primary cursor-pointer"
              data-testid="custom-wallpaper-opacity" />
          </div>

          {/* Blur */}
          <div>
            <Label className="text-[11px] mb-1 block">Blur ({blur}px)</Label>
            <input type="range" min={0} max={20} value={blur}
              onChange={(e) => setBlur(parseInt(e.target.value, 10))}
              onMouseUp={(e) => commit('custom_wallpaper_blur', parseInt(e.target.value, 10))}
              onTouchEnd={(e) => commit('custom_wallpaper_blur', parseInt(e.target.value, 10))}
              onKeyUp={(e) => commit('custom_wallpaper_blur', parseInt(e.target.value, 10))}
              className="w-full accent-primary cursor-pointer"
              data-testid="custom-wallpaper-blur" />
            <p className="text-[10px] text-muted-foreground mt-1">Tip: a little blur (4-8px) makes text on top way more readable.</p>
          </div>
        </div>
      )}
    </div>
  );
};
